import React, { useState } from 'react';
import { FileDown, Loader2, X } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { api } from '../../services/api';
import { i18n } from '../../services/i18n';

const GRADES = ['A', 'B', 'C', 'D', 'E'];
const COLORS = { A: '#009640', B: '#52AE32', C: '#C8D400', D: '#F39200', E: '#E30613' };
export const noiseClass = (db) => (db == null ? null : db <= 68 ? 'A' : db <= 71 ? 'B' : 'C');

const Scale = ({ title, grade, testId }) => (
  <div className="flex-1 min-w-[150px]" data-testid={testId}>
    <p className="text-[11px] font-bold text-gray-800 mb-2">{title}</p>
    <div className="space-y-1">
      {GRADES.map((g, i) => {
        const active = g === grade;
        return (
          <div key={g} className="flex items-center gap-2 h-5">
            <div className="h-full text-white text-[11px] font-black flex items-center pl-1.5" style={{ width: `${40 + i * 9}%`, background: COLORS[g], clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 50%, calc(100% - 8px) 100%, 0 100%)' }}>{g}</div>
            {active && <div className="h-full px-3 bg-black text-white text-xs font-black flex items-center" style={{ clipPath: 'polygon(8px 0, 100% 0, 100% 100%, 8px 100%, 0 50%)' }} data-testid={`${testId}-active`}>{g}</div>}
          </div>
        );
      })}
    </div>
  </div>
);

// Full EU tyre label (Reg. 2020/740 look) shown in a dialog, with PDF download
const EuTyreLabelDialog = ({ product, open, onOpenChange }) => {
  const [busy, setBusy] = useState(false);
  const t = product.tyre || {};
  const label = t.label || {};
  const tr = (k, p) => i18n.t(k, p);
  const nz = label.noise_db;
  const size = `${t.width || ''}/${t.height || ''} R${t.diameter || ''} ${t.load_index || ''}${t.speed_index || ''}`.trim();

  const download = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/products/${product.id}/eu-label.pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = `etiquette-ue-${(product.ref || product.id.slice(0, 8)).replace(/\s/g, '')}.pdf`; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-0 overflow-hidden" data-testid="eu-label-dialog">
        <DialogTitle className="sr-only">{tr('tyre.euLabelFull')}</DialogTitle>
        <div className="border-4 border-black m-4 rounded-sm bg-white">
          <div className="flex items-center gap-3 px-4 py-3" style={{ background: '#003399' }}>
            <div className="text-white">
              <p className="font-black text-sm leading-tight uppercase">{tr('tyre.euLabelFull')}</p>
              <p className="text-[10px] opacity-80">Règlement (UE) 2020/740 · C1</p>
            </div>
            <button type="button" onClick={() => onOpenChange(false)} className="ml-auto text-white/80 hover:text-white" data-testid="eu-label-close"><X size={18} /></button>
          </div>
          <div className="px-4 py-3 border-b border-black">
            <p className="font-black text-gray-900">{product.brand}</p>
            <p className="text-xs text-gray-700">{product.name}</p>
            <p className="font-bold text-sm text-gray-900 mt-1" data-testid="eu-label-size">{size}</p>
          </div>
          <div className="flex flex-wrap gap-6 px-4 py-4 border-b border-black">
            <Scale title={tr('tyre.fuelFull')} grade={(label.fuel || '').toUpperCase()} testId="eu-label-fuel" />
            <Scale title={tr('tyre.wetGrip')} grade={(label.wet || '').toUpperCase()} testId="eu-label-wet" />
          </div>
          <div className="px-4 py-4 border-b border-black flex items-center justify-between gap-4" data-testid="eu-label-noise">
            <div>
              <p className="text-[11px] font-bold text-gray-800">{tr('tyre.noiseFull')}</p>
              <p className="text-2xl font-black text-gray-900">{nz != null ? `${nz} dB` : '—'}</p>
            </div>
            <div className="flex gap-1.5">
              {['A', 'B', 'C'].map((g) => <span key={g} className={`w-9 h-10 rounded-md flex items-center justify-center font-black ${g === noiseClass(nz) ? 'bg-black text-white' : 'bg-gray-200 text-gray-400'}`}>{g}</span>)}
            </div>
          </div>
          <div className="px-4 py-3 text-xs text-gray-700" data-testid="eu-label-snow">
            {t.snowflake ? tr('tyre.snowGrip') : t.ms ? 'M+S' : tr('tyre.noSnowMark')}
          </div>
        </div>
        <div className="px-4 pb-4 flex items-center justify-between gap-3">
          <p className="text-[11px] text-gray-500">{tr('tyre.labelDisclaimer')}</p>
          <Button onClick={download} disabled={busy} className="bg-[#1e3a5f] hover:bg-[#162c48] shrink-0" data-testid="eu-label-download">
            {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <FileDown size={16} className="mr-2" />} PDF
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EuTyreLabelDialog;
